import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { deliveryDoneDispatch, fetchWarehouseDispatch } from "slices/Warehouse/Dispatch/thunk";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { MecordColors } from "Constants/Color";

interface DeliveryDoneModalProps {
    show: boolean;
    handleClose: () => void;
    dispatch_id: string | null;
    warehouseId: string | null;
    pageIndex: number;
    pageSize: number;
    sortField: string;
    sortDirection: string;
    search: string;
    filterValue: any[];
}

const DeliveryDoneModal: React.FC<DeliveryDoneModalProps> = ({
    show,
    handleClose,
    dispatch_id,
    warehouseId,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
}) => {
    const dispatch = useDispatch<any>();

     const getTodayDate = () => {
        const today = new Date();
        const offset = today.getTimezoneOffset();
        const localToday = new Date(today.getTime() - offset * 60 * 1000);
        return localToday.toISOString().split("T")[0];
    };

    const formik = useFormik({
        initialValues: {
            delivery_date: getTodayDate(),
            delivered_by: "",
        },
        validationSchema: Yup.object({
            delivery_date: Yup.string().required("Delivery date is required"),
            delivered_by: Yup.string().required("Delivered by is required"),
        }),
        onSubmit: async (values) => {
            if (!dispatch_id) return;

            try {
                const result = await dispatch(
                    deliveryDoneDispatch({
                        data: values,
                        dispatch_id: dispatch_id,
                    })
                ).unwrap();

                if (result.status) {
                    toast.success("Delivery marked as done successfully");
                    handleClose();
                    // Refresh the list
                    dispatch(
                        fetchWarehouseDispatch({
                            page: pageIndex + 1,
                            pageSize,
                            sort: sortField,
                            order: sortField ? (sortDirection as any) : "",
                            search,
                            filters: filterValue,
                            warehouse_id: warehouseId as any,
                        })
                    );
                } else {
                    toast.error(result.message || "Failed to mark delivery as done");
                }
            } catch (error: any) {
                toast.error(error?.message || "Something went wrong");
            }
        },
    });

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Mark Delivery Done</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={formik.handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="delivery_date"
                            max={getTodayDate()}
                            value={formik.values.delivery_date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={!!(formik.touched.delivery_date && formik.errors.delivery_date)}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.delivery_date}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Delivered By</Form.Label>
                        <Form.Control
                            type="text"
                            name="delivered_by"
                            placeholder="Enter name"
                            value={formik.values.delivered_by}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={!!(formik.touched.delivered_by && formik.errors.delivered_by)}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.delivered_by}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-flex justify-content-center w-100 gap-2">
                        <CancelButton
                            showConfirmation={false}
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                        />
                        <Button type="submit" disabled={formik.isSubmitting} style={{ backgroundColor: MecordColors?.ThemeBGColor, borderColor: MecordColors?.ThemeBGColor }}>
                            {formik.isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default DeliveryDoneModal;
